import { motion } from 'framer-motion';
import { showdownOverlayPosition, type SeatLayoutProfile } from 'constants/seatLayout';
import { displayHandCategory } from 'lib/hand-category';
import { useCurrency } from 'hooks/useCurrency';

export type TableAnnounceMode = 'winner' | 'collect-pot' | 'collect-boot';

interface ShowdownBannerProps {
  mode?: TableAnnounceMode;
  winnerName?: string;
  potAmount?: number;
  handCategory?: string;
  /** @deprecated use mode="collect-pot" */
  isCollecting?: boolean;
  isLocalWinner?: boolean;
  /** overlay = table top center; seat = inline beside player HUD (legacy) */
  variant?: 'overlay' | 'seat';
  layoutProfile?: SeatLayoutProfile;
}

export type ShowdownBannerInfo = Pick<
  ShowdownBannerProps,
  'mode' | 'winnerName' | 'potAmount' | 'handCategory' | 'isLocalWinner'
>;

function resolveMode(props: ShowdownBannerProps): TableAnnounceMode {
  if (props.mode) return props.mode;
  if (props.isCollecting) return 'collect-pot';
  return 'winner';
}

export default function ShowdownBanner({
  mode: modeProp,
  winnerName,
  potAmount,
  handCategory,
  isCollecting = false,
  isLocalWinner: _isLocalWinner = false,
  variant = 'overlay',
  layoutProfile = 'desktop',
}: ShowdownBannerProps) {
  const { format } = useCurrency();
  const mode = resolveMode({ mode: modeProp, isCollecting });
  const amount = Math.max(0, Number(potAmount) || 0);
  const formattedAmount = format(amount, { maximumFractionDigits: 0 });

  let primaryLine: string;
  let secondaryLine: string | undefined;

  if (mode === 'collect-boot') {
    primaryLine = 'Collecting boot amount of';
    secondaryLine = formattedAmount;
  } else if (mode === 'collect-pot') {
    primaryLine = 'Collecting pot amount of';
    secondaryLine = formattedAmount;
  } else {
    primaryLine = `${winnerName ?? 'Player'} wins`;
    secondaryLine = displayHandCategory(handCategory);
  }

  const isSeat = variant === 'seat';

  const banner = (
    <div
      className={`tp-octro-table-announce${isSeat ? ' tp-octro-table-announce-seat' : ''}`}
      role="status"
      aria-live="polite"
    >
      <div className="tp-octro-table-announce-rule" aria-hidden />
      <div className="tp-octro-table-announce-body">
        <p className="tp-octro-table-announce-line">{primaryLine}</p>
        {secondaryLine ? (
          <p
            className={
              mode === 'winner' && handCategory
                ? 'tp-octro-table-announce-sub'
                : 'tp-octro-table-announce-amount'
            }
          >
            {secondaryLine}
          </p>
        ) : null}
      </div>
      <div className="tp-octro-table-announce-rule" aria-hidden />
    </div>
  );

  if (isSeat) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.92, y: 4 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96 }}
        className="tp-showdown-seat-wrap pointer-events-none relative z-[14] w-full max-w-[13rem]"
      >
        {banner}
      </motion.div>
    );
  }

  const overlayPos = showdownOverlayPosition(layoutProfile);

  return (
    <div
      className={`tp-showdown-overlay-wrap tp-showdown-overlay-wrap-${layoutProfile} pointer-events-none absolute z-[58]`}
      style={{
        left: `${overlayPos.x}%`,
        top: `${overlayPos.y}%`,
      }}
    >
      <div className="tp-showdown-overlay-anchor">
        <motion.div
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
          className="tp-showdown-overlay-motion"
        >
          {banner}
        </motion.div>
      </div>
    </div>
  );
}
